<script setup>
import { onMounted, ref, watch } from 'vue';
/*import ArrowIcon from '@assets/icons/arrow.svg';*/
import HomeIcon from '@assets/icons/home.svg';

const props = defineProps({
    items: Object,
    title: String,
    classes: String,
    itemClasses: String,
});

const outer = ref(null);
const links = ref(null);

const sectionInView = ref(null);
const isVisible = ref(false);
const isNavExpanded = ref(false);
const isScrolling = ref(false);
const scrollTimeout = ref(null);

const updateActiveSection = () => {
    // Skip updates during programmatic scrolling
    if (isScrolling.value) {
        return;
    }

    // Dynamic offset based on sticky nav height
    const navHeight = outer.value?.offsetHeight || 0;
    const offset = navHeight + 64; // nav height + some buffer
    const scrollPosition = window.scrollY + offset;

    let activeIndex = null;
    let closestDistance = Infinity;

    // Check visibility with debouncing to prevent flicker
    const pageContent = document.querySelector('#pageContent');
    if (pageContent) {
        const rect = pageContent.getBoundingClientRect();
        const shouldBeVisible =
            rect.bottom > 0 && rect.top + 200 < scrollPosition;

        // Add small delay/hysteresis to prevent rapid toggling
        if (shouldBeVisible !== isVisible.value) {
            const delay = shouldBeVisible ? 0 : 150; // Immediate show, delayed hide
            setTimeout(() => {
                isVisible.value = shouldBeVisible;
            }, delay);
        }
    }

    // Find the section that's closest to our target position
    for (let i = 0; i < props.items.length; i++) {
        const section = document.querySelector(props.items[i].target);
        if (section) {
            const rect = section.getBoundingClientRect();
            const sectionTop = rect.top + window.scrollY;
            const distance = Math.abs(scrollPosition - sectionTop);

            // Section is visible and closer than previous ones
            if (scrollPosition >= sectionTop && distance < closestDistance) {
                activeIndex = i;
                closestDistance = distance;
            }
        }
    }

    // Only update if we have a clear winner
    if (activeIndex !== null) {
        sectionInView.value = activeIndex;
    }
};

const handleLinkClick = (event, target) => {
    event.preventDefault();
    const element = document.querySelector(target);
    if (element) {
        // Set scrolling state to prevent interference
        isScrolling.value = true;

        // Find and set the correct active index immediately
        const targetIndex = props.items.findIndex(
            (item) => item.target === target
        );
        if (targetIndex !== -1) {
            sectionInView.value = targetIndex;
        }

        // Clear existing timeout
        if (scrollTimeout.value) {
            clearTimeout(scrollTimeout.value);
        }

        // Scroll to element
        element.scrollIntoView({ behavior: 'smooth' });

        // Reset scrolling state after animation completes
        scrollTimeout.value = setTimeout(() => {
            isScrolling.value = false;
            updateActiveSection(); // Verify final position
        }, 1000); // Smooth scroll typically takes 500-800ms
    }

    // Close navigation on mobile after link click
    if (isNavExpanded.value) {
        isNavExpanded.value = false;
    }
};

const toggleNavigation = () => {
    isNavExpanded.value = !isNavExpanded.value;
};

onMounted(() => {
    sectionInView.value = null;

    const observer = new IntersectionObserver(
        (entries) => {
            updateActiveSection();
        },
        {
            rootMargin: '-256px 0% -60% 0%',
            threshold: [0, 1],
        }
    );

    // Throttle scroll events for better performance
    let scrollThrottle = null;
    const throttledScrollHandler = () => {
        if (scrollThrottle === null) {
            scrollThrottle = requestAnimationFrame(() => {
                updateActiveSection();
                scrollThrottle = null;
            });
        }
    };

    window.addEventListener('scroll', throttledScrollHandler);
    updateActiveSection();

    props.items.forEach((item) => {
        const section = document.querySelector(item.target);

        if (section) {
            observer.observe(section);
        }
    });

    return () => {
        observer.disconnect();
        window.removeEventListener('scroll', throttledScrollHandler);
        if (scrollThrottle) {
            cancelAnimationFrame(scrollThrottle);
        }
    };
});

watch(sectionInView, (newIndex, oldIndex) => {
    if (newIndex && newIndex !== oldIndex) {
        links.value[newIndex]?.scrollIntoView({
            behavior: 'smooth',
            container: 'nearest',
        });
    }
});
</script>

<template>
    <nav
        :aria-label="$t('Table of contents')"
        ref="outer"
        class="bg-primary text-nav fixed top-0 right-0 left-0 z-50 overflow-x-auto py-2 lg:whitespace-nowrap transition-transform duration-300 ease-out lg:py-4 shadow-sm"
        :class="[
            classes,
            isVisible
                ? 'translate-y-0'
                : 'pointer-events-none -translate-y-full',
        ]"
    >
        <div class="mx-auto max-w-[1920px] px-5 lg:overflow-x-auto">
            <div class="flex justify-start lg:gap-4">
                <a
                    href="/"
                    class="text-primary-foreground hover:text-foreground mt-1 flex !size-10 items-center justify-center sm:mt-1.5 lg:mt-0"
                >
                    <HomeIcon
                        class="block size-6 fill-current lg:size-8"
                        aria-label="Home"
                    />
                </a>

                <div
                    class="flex w-full flex-col justify-center lg:min-w-max lg:flex-row lg:items-center lg:px-0"
                >
                    <button
                        @click="toggleNavigation"
                        class="group text-primary-foreground my-2 flex items-center justify-between px-4 lg:hidden"
                        :aria-expanded="isNavExpanded"
                        aria-controls="toc-navigation"
                        tabindex="-1"
                        aria-label="Navigation auf dieser Seite ein-/ausklappen"
                    >
                        <span class="text-primary-foreground text-sm font-bold"
                            >Auf dieser Seite</span
                        >
                        <svg
                            class="ml-2 h-4 w-4 transition-transform"
                            :class="{ 'rotate-180': isNavExpanded }"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                    <div
                        id="toc-navigation"
                        class="grid min-h-0 overflow-hidden transition-[grid-template-rows] lg:contents"
                        :class="
                            isNavExpanded
                                ? 'grid-rows-[1fr]'
                                : 'grid-rows-[0fr]'
                        "
                    >
                        <ul
                            class="flex min-h-0 flex-col items-start justify-center gap-x-4 gap-y-2 lg:flex-row lg:whitespace-nowrap xl:gap-x-8"
                        >
                            <li
                                v-for="(item, i) in items"
                                :key="i"
                                ref="links"
                                class="last:mb-4 last:sm:mb-0 lg:flex-shrink-0"
                            >
                                <a
                                    :href="item.target"
                                    @click="
                                        handleLinkClick($event, item.target)
                                    "
                                    class="hover:text-foreground px-4 text-base lg:px-0"
                                    tabindex="-1"
                                    :class="
                                        sectionInView === i
                                            ? 'text-black'
                                            : 'text-primary-foreground'
                                    "
                                    v-html="item.title"
                                ></a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </nav>
</template>

<style scoped>
.sticky-element {
    transition: opacity 0.5s ease-in-out;
}

.sticky-visible {
    top: 0;
    position: sticky;
}
</style>
